import { useEffect, useMemo, useState } from 'react';
import LogRocket from 'logrocket';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';

import { readTopic, updateTopicVisibility } from '@api/narratives';

import { flowTopicTag, narrativeErrorStatuses, narrativeStatus } from '@helpers/narratives';

import { LoaderSkeleton, TopicCard } from '@utilities';

import {
  applyDraftChartColors,
  getDefaultTopicData,
  sanitizeDraftDataset,
} from '../../utilities/helpers';
import TopicCardFooter from '../TopicCardFooter';
import Visualization from '../Visualization';

import SampleSizeFooter from './components/SampleSizeFooter';
import SubscribedFilterFooter from './components/SubscribedFilterFooter';

import styles from './_index.module.scss';

const renderDraftLoadingSkeleton = () => (
  <LoaderSkeleton height={210}>
    <rect height="10" rx="7" ry="7" width="90%" x="0" y="0" />
    <rect height="10" rx="7" ry="7" width="90%" x="0" y="30" />
    <rect height="30" rx="15" ry="15" width="100%" x="0" y="60" />
    <rect height="30" rx="15" ry="15" width="100%" x="0" y="120" />
    <rect height="30" rx="15" ry="15" width="100%" x="0" y="180" />
  </LoaderSkeleton>
);

const NarrativeTopicCard = ({
  initialTopicData = null,
  isDraftCard = false,
  isPreviewActive = false,
  keyIndex,
  onPreview,
  publicToken,
  setDownloadData,
  subscribedCategories,
  topic,
  topicControls = null,
  useWindowScroll,
}) => {
  const [topicData, setTopicData] = useState(initialTopicData || getDefaultTopicData(topic));
  const topicTags = topicData?.tags || {};
  const includesSubscribedCategories = flowTopicTag.INCLUDES_SUBSCRIBED_CATEGORIES in topicTags;
  const isNoSample = flowTopicTag.NO_SAMPLE in topicTags;
  const isPreviewCard = flowTopicTag.PREVIEW in topicTags;
  const [isExcluded, setIsExcluded] = useState(topic.isHidden || isNoSample || false);
  const resolvedTopicControls = topicControls;

  const exportData = useMemo(() => {
    return {
      reportType: 'Narrative',
      tabName: topicData?.topicId,
    };
  }, [topicData]);
  const sampleSizes = !isDraftCard ? topicData?.visualization?.sample_size || null : null;

  useEffect(() => {
    if (!initialTopicData) return;
    setTopicData(initialTopicData);
  }, [initialTopicData]);

  useEffect(() => {
    setIsExcluded(topic.isHidden || isNoSample || false);
  }, [topic.isHidden, isNoSample]);

  useEffect(() => {
    if (
      isDraftCard ||
      topicData?.status !== narrativeStatus.COMPLETED ||
      !topicData?.id ||
      isPreviewActive
    )
      return;

    // Initialize downloadData with topic information
    setDownloadData((prevData) => {
      const data = new Map(prevData);
      const topicId = topicData?.id;
      const existingTopic = data.get(topicId);

      data.set(topicId, {
        ...existingTopic,
        id: topicId,
        title: topicData?.visualization?.clean_title || topicData?.title,
      });
      return data;
    });
  }, [isDraftCard, topicData, isPreviewActive, setDownloadData]);

  useEffect(() => {
    if (isDraftCard) return;

    const controller = new AbortController();

    if (!topicData?.loading) {
      return () => {
        controller.abort();
      };
    }

    const fetchTopic = async () => {
      try {
        const result = await readTopic({ id: topic.id, publicToken, signal: controller.signal });
        if (result.status === narrativeStatus.COMPLETED) {
          setTopicData((prevTopicData) => ({
            ...prevTopicData,
            ...result,
            error: false,
            loading: false,
          }));
        } else {
          setTopicData((prevTopicData) => ({
            ...prevTopicData,
            error: true,
            loading: false,
          }));
        }
      } catch (error) {
        console.error(error);
        setTopicData((prevTopicData) => ({
          ...prevTopicData,
          error: true,
          loading: false,
        }));
      }
    };

    if (topic?.status === narrativeStatus.COMPLETED) {
      fetchTopic();
    } else if (narrativeErrorStatuses.includes(topic?.status)) {
      setTopicData((prevTopicData) => ({
        ...prevTopicData,
        error: true,
        loading: false,
      }));
    }

    return () => {
      controller.abort();
    };
  }, [isDraftCard, topicData?.loading, topic?.status]);

  const handleToggleVisibility = async () => {
    try {
      await updateTopicVisibility({ id: topic.id, isHidden: !isExcluded });
      setIsExcluded((prev) => !prev);
      LogRocket.track('Flow_Topic_Visibility_Toggle', { id: topic?.id, topic: topic?.topicId });
    } catch (error) {
      console.error('Error updating topic visibility:', error);
    }
  };

  if (isExcluded && publicToken) return null;

  if (topicData?.error) {
    return publicToken ? null : (
      <TopicCard
        footer={
          <span>
            <strong>Excluded </strong>
            This topic is unavailable because of an internal error.
          </span>
        }
        isDraft={isDraftCard}
        isExcluded
        key={`topic-card-${keyIndex}`}
        title={topic?.title}
      />
    );
  }

  if (topicData?.loading) {
    return (
      <TopicCard
        footer={
          <TopicCardFooter
            handleToggleVisibility={handleToggleVisibility}
            initialFeedback={topic?.feedback}
            isDraftCard={isDraftCard}
            isExcluded={isExcluded}
            isLoading={topicData?.loading}
            isPreviewActive={isPreviewActive}
            isPreviewCard={isPreviewCard}
            topicControls={resolvedTopicControls}
            topicId={topic?.id}
          />
        }
        isDraft
        isExcluded={isExcluded && !isPreviewActive}
        key={`topic-card-${keyIndex}`}
        title={topic?.title}
      >
        <div className={styles['card-body']}>
          <div
            className={styles['card-processing']}
            key={`topic-card-${keyIndex}-dataset-${keyIndex}`}
          >
            {renderDraftLoadingSkeleton()}
          </div>
          {!isDraftCard && (
            <div className={styles['loading-indicator-text']}>
              Processing
              <span className={styles['loading-indicator-dot']}>.</span>
              <span className={styles['loading-indicator-dot']}>.</span>
              <span className={styles['loading-indicator-dot']}>.</span>
            </div>
          )}
        </div>
      </TopicCard>
    );
  }

  if (
    topicData?.visualization?.datasets?.length === 0 &&
    topicData?.status === narrativeStatus.COMPLETED
  ) {
    return (
      <TopicCard
        footer={
          <TopicCardFooter
            handleToggleVisibility={handleToggleVisibility}
            initialFeedback={topic?.feedback}
            isDraftCard={isDraftCard}
            isExcluded={isExcluded}
            isLoading={topicData?.loading}
            isPreviewCard={isPreviewCard}
            topicControls={resolvedTopicControls}
            topicId={topic?.id}
          />
        }
        isDraft={isDraftCard}
        isExcluded={isExcluded}
        key={`topic-card-${keyIndex}`}
        title={topic?.title}
      >
        <small>No topic data available.</small>
      </TopicCard>
    );
  }

  if (isNoSample) {
    return publicToken ? null : (
      <TopicCard
        footer={
          <span>
            <strong>Excluded </strong>
            {topicData?.tags[flowTopicTag.NO_SAMPLE]}
          </span>
        }
        isDraft={isDraftCard}
        isExcluded
        key={`topic-card-${keyIndex}`}
        title={topic?.title}
      />
    );
  }

  const shouldRenderFooter = !publicToken && (!isDraftCard || Boolean(resolvedTopicControls));
  const footerContent = shouldRenderFooter ? (
    <TopicCardFooter
      handleToggleVisibility={
        isPreviewCard ? () => onPreview(topic?.id, topicData) : handleToggleVisibility
      }
      initialFeedback={topic?.feedback}
      isDraftCard={isDraftCard}
      isExcluded={isExcluded && !isPreviewActive}
      isLoading={topicData?.loading}
      isPreviewActive={isPreviewActive}
      isPreviewCard={isPreviewCard}
      topicControls={resolvedTopicControls}
      topicId={topic?.id}
    />
  ) : null;

  return (
    <TopicCard
      footer={footerContent}
      isDraft={isDraftCard}
      isExcluded={isExcluded && !isPreviewActive}
      key={`topic-card-${keyIndex}`}
      title={topic?.title}
    >
      <div className={styles['card-body']}>
        {topicData?.visualization?.datasets?.map((dataset, index) => {
          const shouldMaskDraftValues = isDraftCard && !dataset?.isDraftDisplayData;
          const resolvedDataset = shouldMaskDraftValues ? sanitizeDraftDataset(dataset) : dataset;
          const visualizationDataset = isDraftCard
            ? applyDraftChartColors(resolvedDataset)
            : resolvedDataset;
          const topicTakeaways =
            !isDraftCard && dataset?.readAs ? (
              <div className={styles['topic-takeaways']}>
                <ReactMarkdown>{dataset?.readAs}</ReactMarkdown>
              </div>
            ) : null;

          return (
            <div
              className={styles['card-visualization']}
              key={`topic-card-${keyIndex}-dataset-${index}`}
            >
              <div className={styles['card-visual']}>
                <Visualization
                  dataset={visualizationDataset}
                  exportData={exportData}
                  isDraftCard={isDraftCard}
                  isExcluded={isExcluded}
                  key={`topic-card-${keyIndex}-visualization-${index}`}
                  setDownloadData={setDownloadData}
                  topicId={topicData?.id}
                  topicTakeaways={topicTakeaways}
                  useWindowScroll={useWindowScroll}
                />
              </div>
            </div>
          );
        })}
        {sampleSizes && <SampleSizeFooter sampleSizes={sampleSizes} />}
        {includesSubscribedCategories && <SubscribedFilterFooter answers={subscribedCategories} />}
      </div>
    </TopicCard>
  );
};

NarrativeTopicCard.propTypes = {
  initialTopicData: PropTypes.object,
  isDraftCard: PropTypes.bool,
  isPreviewActive: PropTypes.bool,
  keyIndex: PropTypes.number,
  onPreview: PropTypes.func,
  publicToken: PropTypes.string,
  setDownloadData: PropTypes.func,
  subscribedCategories: PropTypes.array,
  topic: PropTypes.object,
  topicControls: PropTypes.shape({
    primaryAction: PropTypes.shape({
      disabled: PropTypes.bool,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
    }),
    secondaryActions: PropTypes.arrayOf(
      PropTypes.shape({
        disabled: PropTypes.bool,
        label: PropTypes.string.isRequired,
        onClick: PropTypes.func,
      })
    ),
  }),
  useWindowScroll: PropTypes.bool,
};
export default NarrativeTopicCard;
